// src/pages/marketplace/MarketplaceReviewsPage.tsx
// Full reviews page for a product with rating summary, filters, and review form.

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Textarea,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Alert,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Render filled/empty stars for a given rating (0-5). */
function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClass = size === 'lg' ? 'w-6 h-6' : size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
  const full = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          className={`${sizeClass} ${i < full ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
        </svg>
      ))}
    </span>
  );
}

/** Clickable star rating input. */
function StarRatingInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (rating: number) => void;
}) {
  const [hovered, setHovered] = useState(0);
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => {
        const starNum = i + 1;
        const filled = hovered ? starNum <= hovered : starNum <= value;
        return (
          <button
            key={i}
            type="button"
            onMouseEnter={() => setHovered(starNum)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(starNum)}
            className="focus:outline-none"
            aria-label={`${starNum} star${starNum > 1 ? 's' : ''}`}
          >
            <svg
              className={`w-7 h-7 transition-colors ${
                filled ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-200'
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
            </svg>
          </button>
        );
      })}
    </span>
  );
}

type SortOption = 'newest' | 'highest' | 'lowest';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MarketplaceReviewsPage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();
  const { slug } = useParams<{ slug: string }>();

  const [product, setProduct] = useState<any>(null);
  const [marketplace, setMarketplace] = useState<any>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [filterStars, setFilterStars] = useState<number>(0); // 0 = all
  const [cursor, setCursor] = useState('');

  // Review form state
  const [formTitle, setFormTitle] = useState('');
  const [formRating, setFormRating] = useState(0);
  const [formBody, setFormBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Load product and reviews
  useEffect(() => {
    if (!slug) return;
    setLoading(true);

    const load = async () => {
      try {
        const prod: any = (await store.GetProductBySlug(slug)) ?? store.currentItem;
        setProduct(prod);

        if (prod?.entity?.id) {
          const [mkRes, revRes] = await Promise.allSettled([
            store.GetMarketplaceMetadata(prod.entity.id),
            store.ListProductReviews(prod.entity.id),
          ]);

          if (mkRes.status === 'fulfilled') {
            setMarketplace(mkRes.value?.marketplace ?? null);
          }
          if (revRes.status === 'fulfilled') {
            const reviewItems = revRes.value?.reviews?.items;
            setReviews(Array.isArray(reviewItems) ? reviewItems : []);
          }
        }
      } catch {
        // Store handles errors
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug, cursor]);

  // Submit review
  const handleSubmitReview = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!product?.entity?.id || formRating === 0) return;

      setSubmitting(true);
      setSubmitSuccess(false);

      try {
        await store.CreateProductReview(product.entity.id, {
          title: formTitle.trim(),
          body: formBody.trim(),
          rating: formRating,
        });
        setSubmitSuccess(true);
        setFormTitle('');
        setFormRating(0);
        setFormBody('');

        // Reload reviews
        const revRes = await store.ListProductReviews(product.entity.id);
        const reloaded = revRes?.reviews?.items;
        setReviews(Array.isArray(reloaded) ? reloaded : []);
      } catch {
        // Store handles errors
      } finally {
        setSubmitting(false);
      }
    },
    [product, formTitle, formRating, formBody, store],
  );

  // Client-side sort and filter
  let filteredReviews = [...reviews];
  if (filterStars > 0) {
    filteredReviews = filteredReviews.filter((r: any) => r.rating === filterStars);
  }
  if (sortBy === 'highest') {
    filteredReviews.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
  } else if (sortBy === 'lowest') {
    filteredReviews.sort((a, b) => (a.rating ?? 0) - (b.rating ?? 0));
  } else {
    filteredReviews.sort(
      (a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime(),
    );
  }

  // Rating breakdown
  const avgRating = marketplace?.average_rating ?? 0;
  const reviewCount = marketplace?.review_count ?? reviews.length;
  const ratingBreakdown = [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    count: reviews.filter((r: any) => r.rating === stars).length,
  }));
  const maxBreakdown = Math.max(...ratingBreakdown.map((b) => b.count), 1);

  // ---------------------------------------------------------------------------
  // Loading / Error
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (!product) {
    return (
      <Alert variant="warning" title={t('marketplace.productNotFound')}>
        <Link to="/marketplace" className="mt-2 inline-block text-sm underline text-primary">
          {t('marketplace.backToMarketplace')}
        </Link>
      </Alert>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/marketplace" className="text-muted-foreground hover:text-foreground">
          {t('marketplace.title')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link
          to={`/marketplace/${slug}`}
          className="text-muted-foreground hover:text-foreground"
        >
          {product.name}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('marketplace.reviews')}</span>
      </nav>

      {/* Product mini header */}
      <div className="flex items-center gap-4">
        {product.icon_url ? (
          <img src={product.icon_url} alt={product.name} className="w-12 h-12 rounded-xl object-cover" />
        ) : (
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
            style={{ backgroundColor: product.color_primary || '#6366f1' }}
          >
            {(product.name ?? '?')[0]?.toUpperCase()}
          </div>
        )}
        <div>
          <h1 className="text-xl font-bold text-foreground">{product.name}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <StarRating rating={avgRating} size="sm" />
            <span className="text-sm text-muted-foreground">
              {avgRating.toFixed(1)} ({reviewCount} {t('marketplace.reviews')})
            </span>
          </div>
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Rating summary */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-8">
            {/* Big average */}
            <div className="text-center shrink-0">
              <div className="text-5xl font-bold text-foreground">{avgRating.toFixed(1)}</div>
              <StarRating rating={avgRating} size="md" />
              <p className="text-sm text-muted-foreground mt-1">
                {reviewCount} {t('marketplace.reviews')}
              </p>
            </div>
            {/* Breakdown bars */}
            <div className="flex-1 space-y-2 w-full">
              {ratingBreakdown.map(({ stars, count }) => (
                <button
                  key={stars}
                  onClick={() => setFilterStars(filterStars === stars ? 0 : stars)}
                  className={`flex items-center gap-3 w-full rounded px-2 py-0.5 transition-colors ${
                    filterStars === stars ? 'bg-primary/10' : 'hover:bg-muted'
                  }`}
                >
                  <span className="text-sm text-muted-foreground w-4 text-right">{stars}</span>
                  <svg className="w-4 h-4 text-yellow-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
                  </svg>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 rounded-full transition-all"
                      style={{ width: `${(count / maxBreakdown) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground w-8">{count}</span>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Write a Review form */}
      <Card>
        <CardHeader>
          <CardTitle>{t('marketplace.writeReview')}</CardTitle>
        </CardHeader>
        <CardContent>
          {submitSuccess && (
            <Alert variant="success" className="mb-4" dismissible onDismiss={() => setSubmitSuccess(false)}>
              {t('marketplace.reviewSubmitted')}
            </Alert>
          )}
          <form onSubmit={handleSubmitReview} className="space-y-4">
            {/* Star rating input */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('marketplace.yourRating')} *
              </label>
              <StarRatingInput value={formRating} onChange={setFormRating} />
              {formRating === 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('marketplace.clickToRate')}
                </p>
              )}
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('marketplace.reviewTitle')}
              </label>
              <Input
                placeholder={t('marketplace.reviewTitlePlaceholder')}
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
              />
            </div>

            {/* Body */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('marketplace.reviewBody')}
              </label>
              <Textarea
                placeholder={t('marketplace.reviewBodyPlaceholder')}
                value={formBody}
                onChange={(e) => setFormBody(e.target.value)}
                rows={5}
              />
            </div>

            <Button type="submit" disabled={formRating === 0 || submitting}>
              {submitting
                ? t('common.submitting')
                : t('marketplace.submitReview')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Filter & sort row */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">
            {filteredReviews.length} {t('marketplace.reviews')}
            {filterStars > 0 && (
              <span>
                {' '}{t('marketplace.withStars')} {filterStars} {t('marketplace.stars')}
                <button
                  onClick={() => setFilterStars(0)}
                  className="ml-2 text-xs text-primary hover:underline"
                >
                  {t('marketplace.clearFilter')}
                </button>
              </span>
            )}
          </p>
        </div>
        <Select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortOption)} fullWidth={false}>
          <option value="newest">{t('marketplace.sortNewest')}</option>
          <option value="highest">{t('marketplace.sortHighest')}</option>
          <option value="lowest">{t('marketplace.sortLowest')}</option>
        </Select>
      </div>

      {/* Review list */}
      {filteredReviews.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {filterStars > 0
                ? t('marketplace.noReviewsWithFilter')
                : t('marketplace.noReviews')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredReviews.map((review: any) => (
            <Card key={review.entity?.id || Math.random()}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  {/* Author avatar placeholder */}
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-medium text-muted-foreground shrink-0">
                    {(review.author?.display_name ?? 'A')[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-foreground">
                        {review.author?.display_name || t('marketplace.anonymous')}
                      </span>
                      <StarRating rating={review.rating ?? 0} />
                      <span className="text-xs text-muted-foreground">
                        {review.created_at
                          ? new Date(review.created_at).toLocaleDateString()
                          : ''}
                      </span>
                    </div>
                    {review.title && (
                      <h4 className="text-sm font-semibold text-foreground mb-1">
                        {review.title}
                      </h4>
                    )}
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {review.body}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {reviews.length >= 50 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          {cursor && (
            <Button variant="secondary" size="sm" onClick={() => setCursor('')}>
              {t('common.previous')}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const lastItem = reviews[reviews.length - 1];
              if (lastItem) {
                setCursor(lastItem.entity?.id || '');
              }
            }}
          >
            {t('common.next')}
          </Button>
        </div>
      )}
    </div>
  );
});

export default MarketplaceReviewsPage;
